import React, { useState, useEffect } from "react";
import { hexToCSSFilter } from "hex-to-css-filter";
import "../assets/css/OrderTypePopup.css";

import counterPickupSvg from "../assets/web/counter-pickup.svg";
import dineInSvg from "../assets/web/dinein.svg";
import deliverySvg from "../assets/web/delivery.svg";
import driveThroughSvg from "../assets/web/take-away.svg";

const OrderTypePopup = ({
  open,
  onClose,
  onConfirm,
  orderType,
  storeInfo = {},
  storeInfoConfig = {},
}) => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 900px)").matches
      : true
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(min-width: 900px)");
    const listener = (e) => setIsDesktop(e.matches);
    if (media.addEventListener) {
      media.addEventListener("change", listener);
    } else {
      media.addListener(listener);
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", listener);
      } else {
        media.removeListener(listener);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Do not show popup if not open or not on desktop view
  if (!open || !isDesktop) return null;

  const primaryColor =
    storeInfoConfig?.general_config?.primary_color ||
    storeInfo?.primary_color ||
    "#E8450A";

  const primaryFilter = (() => {
    try {
      if (!primaryColor) return "";
      const res = hexToCSSFilter(primaryColor);
      return res?.filter ? `brightness(0) saturate(100%) ${res.filter}` : "";
    } catch (e) {
      return "";
    }
  })();

  const code = (
    typeof orderType === "string"
      ? orderType
      : orderType?.order_code || orderType?.order_type || ""
  ).toLowerCase();

  const rawTypeName = (
    typeof orderType === "string"
      ? orderType
      : orderType?.order_type || ""
  ).toLowerCase();

  let title = "Order Type";
  let icon = counterPickupSvg;
  let subtitle = null;
  let alertMessage = "";

  const storeName =
    storeInfo?.store_name || storeInfo?.store_org_name || "";
  const storeAddress =
    storeInfo?.store_ad1 ||
    storeInfo?.address ||
    "";

  const locationLabel = [storeName, storeAddress].filter(Boolean).join(", ");

  const isDineIn = code === "di" || code === "dinein" || code === "dine in" || rawTypeName.includes("dine");

  if (code === "ta" || code === "takeaway" || code === "take away" || rawTypeName.includes("take away") || rawTypeName.includes("takeaway")) {
    title = "Takeaway";
    icon = counterPickupSvg;
    subtitle = (
      <>
        Grab your order at the counter inside <br />
        {locationLabel && (
          <span style={{ fontWeight: 600, color: "#333333" }}>
            {locationLabel}
          </span>
        )}
      </>
    );
    alertMessage =
      "Please arrive within 15 minutes or your order may be remade.";
  } else if (isDineIn) {
    title = "Dine-In Order";
    icon = dineInSvg;
    subtitle = "Enjoy your meal at our café - a server will bring it to your table";
    alertMessage = "";
  } else if (
    code === "del" ||
    code === "delivery" ||
    rawTypeName.includes("car") ||
    rawTypeName.includes("drive")
  ) {
    const isDriveThrough =
      rawTypeName.includes("car") ||
      rawTypeName.includes("drive") ||
      code === "drive_through";
    title = isDriveThrough ? "In car Pickup" : "Delivery";
    icon = isDriveThrough ? driveThroughSvg : deliverySvg;
    subtitle = isDriveThrough
      ? "Stay in your car - we'll bring your order out"
      : "Your order will be delivered directly to your address";
    alertMessage = "";
  } else {
    title = orderType?.order_type || "Confirm Order Type";
    icon = counterPickupSvg;
    subtitle = `Confirm selection for ${orderType?.order_type || "this order type"}`;
    alertMessage = "";
  }

  const hoverBg = primaryColor.startsWith("#")
    ? `${primaryColor}0A`
    : "rgba(232, 69, 10, 0.04)";

  return (
    <div
      className="order-type-popup-overlay"
      onClick={onClose}
    >
      <div
        className="order-type-popup-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Full-Width Header Row with Edge-to-Edge Divider */}
        <div className="order-type-popup-header">
          <h3
            className="order-type-popup-title"
            style={{ color: primaryColor }}
          >
            {title}
          </h3>
          <button
            className="order-type-popup-close-btn"
            onClick={onClose}
            style={{ color: primaryColor }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body Content with Side & Bottom Padding */}
        <div className="order-type-popup-body">
          {/* Main SVG Logo Icon */}
          <div className="order-type-popup-icon-wrapper">
            <img
              src={icon}
              alt={title}
              className="order-type-popup-icon-img"
              style={{ filter: primaryFilter }}
            />
          </div>

          {/* Subtitle / Description */}
          <div
            className="order-type-popup-subtitle"
            style={{
              marginBottom: alertMessage ? "16px" : "24px",
              maxWidth: isDineIn ? "251px" : "340px",
            }}
          >
            {subtitle}
          </div>

          {/* Warning/Alert Banner if present */}
          {alertMessage && (
            <div
              className="order-type-popup-alert"
              style={{ backgroundColor: `${primaryColor}1A` }}
            >
              <div
                className="order-type-popup-alert-icon"
                style={{ backgroundColor: primaryColor }}
              >
                i
              </div>
              <p
                className="order-type-popup-alert-text"
                style={{ color: primaryColor }}
              >
                {alertMessage}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="order-type-popup-actions">
            <button
              className="order-type-popup-btn order-type-popup-btn-outlined"
              onClick={onClose}
              style={{
                border: `2px solid ${primaryColor}`,
                color: primaryColor,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              Go Back
            </button>
            <button
              className="order-type-popup-btn order-type-popup-btn-contained"
              onClick={onConfirm}
              style={{
                backgroundColor: primaryColor,
                color: "#ffffff",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTypePopup;
